import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sparkles, FileText, Download, Send, Menu, X, Settings, LogOut } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const DisputeCenter = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [generatedLetter, setGeneratedLetter] = useState('');
  const [showLetterModal, setShowLetterModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    bureau: '',
    account_name: '',
    account_number: '',
    dispute_reason: '',
    user_name: user?.full_name || '',
    user_address: ''
  });

  useEffect(() => {
    fetchDisputes();
  }, []);

  const fetchDisputes = async () => {
    try {
      const response = await axios.get(`${API}/disputes`);
      setDisputes(response.data);
    } catch (error) {
      toast.error('Failed to load disputes');
    }
  };

  const handleGenerateLetter = async () => {
    if (!formData.bureau || !formData.account_name || !formData.dispute_reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai/generate-letter`, formData);
      setGeneratedLetter(response.data.letter);
      setShowLetterModal(true);
      toast.success('Dispute letter generated!');
    } catch (error) {
      toast.error('Failed to generate letter');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDispute = async () => {
    try {
      await axios.post(`${API}/disputes`, {
        bureau: formData.bureau,
        account_name: formData.account_name,
        account_number: formData.account_number,
        dispute_reason: formData.dispute_reason
      });
      toast.success('Dispute saved successfully!');
      setShowCreateModal(false);
      setShowLetterModal(false);
      fetchDisputes();
      setFormData({
        bureau: '',
        account_name: '',
        account_number: '',
        dispute_reason: '',
        user_name: user?.full_name || '',
        user_address: ''
      });
    } catch (error) {
      toast.error('Failed to save dispute');
    }
  };

  const downloadLetter = () => {
    const blob = new Blob([generatedLetter], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispute-letter-${Date.now()}.txt`;
    a.click();
    toast.success('Letter downloaded!');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-heading font-bold text-gradient">DISPUTEKING.AI</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate('/dashboard')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Dashboard</button>
              <button onClick={() => navigate('/disputes')} className="text-violet-600 font-medium">Disputes</button>
              <button onClick={() => navigate('/education')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Learn</button>
              <button onClick={() => navigate('/settings')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="font-heading">
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-4">
              <button onClick={() => navigate('/dashboard')} className="block w-full text-left text-slate-600 font-medium">Dashboard</button>
              <button onClick={() => navigate('/disputes')} className="block w-full text-left text-violet-600 font-medium">Disputes</button>
              <button onClick={() => navigate('/education')} className="block w-full text-left text-slate-600 font-medium">Learn</button>
              <button onClick={() => navigate('/settings')} className="block w-full text-left text-slate-600 font-medium">Settings</button>
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="w-full justify-start">Logout</Button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-2">Dispute Center</h1>
            <p className="text-slate-600">Generate and track your credit disputes</p>
          </div>
          <Button
            data-testid="new-dispute-button"
            onClick={() => setShowCreateModal(true)}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading font-semibold shadow-lg hover:scale-105 transition-all"
          >
            <FileText className="w-5 h-5 mr-2" />
            New Dispute
          </Button>
        </div>

        {/* Disputes List */}
        {disputes.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border-2 border-dashed border-slate-200">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-heading font-semibold mb-2">No disputes yet</h3>
            <p className="text-slate-600 mb-6">Create your first AI-powered dispute letter</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
            >
              Create First Dispute
            </Button>
          </div>
        ) : (
          <div className="grid gap-6">
            {disputes.map((dispute) => (
              <div key={dispute.id} className="bg-white rounded-3xl p-6 border-2 border-slate-100 hover:border-violet-200 transition-all">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-heading font-semibold mb-1">{dispute.account_name}</h3>
                    <p className="text-sm text-slate-600">{dispute.bureau} • Account #{dispute.account_number}</p>
                  </div>
                  <span className={`px-4 py-1 rounded-full text-sm font-medium ${getStatusColor(dispute.status)}`}>
                    {dispute.status}
                  </span>
                </div>
                <p className="text-slate-700 mb-4">{dispute.dispute_reason}</p>
                <div className="text-sm text-slate-500">
                  Created {new Date(dispute.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Dispute Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold">Create New Dispute</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Credit Bureau *</Label>
              <Select value={formData.bureau} onValueChange={(value) => setFormData({ ...formData, bureau: value })}>
                <SelectTrigger data-testid="bureau-select" className="rounded-xl">
                  <SelectValue placeholder="Select bureau" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Experian">Experian</SelectItem>
                  <SelectItem value="Equifax">Equifax</SelectItem>
                  <SelectItem value="TransUnion">TransUnion</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Account Name *</Label>
              <Input
                data-testid="account-name-input"
                value={formData.account_name}
                onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                placeholder="e.g., Capital One Credit Card"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Account Number</Label>
              <Input
                data-testid="account-number-input"
                value={formData.account_number}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                placeholder="Last 4 digits"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Your Name *</Label>
              <Input
                value={formData.user_name}
                onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Your Address</Label>
              <Input
                value={formData.user_address}
                onChange={(e) => setFormData({ ...formData, user_address: e.target.value })}
                placeholder="123 Main St, City, State ZIP"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Dispute Reason *</Label>
              <Textarea
                data-testid="dispute-reason-textarea"
                value={formData.dispute_reason}
                onChange={(e) => setFormData({ ...formData, dispute_reason: e.target.value })}
                placeholder="Explain why this item is incorrect or inaccurate"
                className="rounded-xl min-h-[100px]"
              />
            </div>
            <Button
              data-testid="generate-letter-button"
              onClick={handleGenerateLetter}
              disabled={loading}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full py-6 font-heading font-semibold"
            >
              {loading ? 'Generating...' : 'Generate AI Dispute Letter'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generated Letter Modal */}
      <Dialog open={showLetterModal} onOpenChange={setShowLetterModal}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold">Your Dispute Letter</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="bg-slate-50 rounded-xl p-6 mb-4 whitespace-pre-wrap font-mono text-sm">
              {generatedLetter}
            </div>
            <div className="flex gap-4">
              <Button
                data-testid="download-letter-button"
                onClick={downloadLetter}
                variant="outline"
                className="flex-1 rounded-full font-heading"
              >
                <Download className="w-5 h-5 mr-2" />
                Download
              </Button>
              <Button
                data-testid="save-dispute-button"
                onClick={handleSaveDispute}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
              >
                <Send className="w-5 h-5 mr-2" />
                Save & Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DisputeCenter;